import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, Loader2, ShieldCheck, AlertTriangle, Clock } from "lucide-react";
import { api, fmt, fmtDate, fmtDateLong, fmtSigned, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  OPEN: "border-amber-500/40 text-amber-400",
  PARTIAL: "border-amber-500/40 text-amber-400",
  PAID: "border-emerald-500/40 text-emerald-400",
  OVERDUE: "border-red-500/40 text-red-400",
  SETTLED: "border-emerald-500/40 text-emerald-400",
  APPLIED: "border-strong text-2",
};

function StatCard({ label, value, tone = "text-1", testid }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-3">{label}</p>
      <p className={`font-display mt-2 text-2xl font-semibold tracking-tight ${tone}`} data-testid={testid}>{value}</p>
    </div>
  );
}

export default function AccountCreditPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [statement, setStatement] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("upi");
  const [paying, setPaying] = useState(false);

  const load = () => {
    api.get("/credit/account").then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e, "Couldn't load PAQVERO Credit")));
    api.get("/credit/statement").then(({ data }) => setStatement(data)).catch(() => setStatement(null));
  };
  useEffect(() => { if (user) load(); }, [user]);

  const pay = async () => {
    const amount = parseFloat(payAmount);
    if (!(amount > 0)) { toast.error("Enter an amount to pay"); return; }
    setPaying(true);
    try {
      const { data } = await api.post("/credit/pay", { amount, method: payMethod });
      toast.success(`Payment received — outstanding is now ${fmt(data.outstanding)}`);
      setPayAmount("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Payment failed"));
    } finally {
      setPaying(false);
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <button onClick={() => navigate("/login", { state: { from: "/account/credit" } })} className="rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-black" data-testid="credit-login-cta">
          Sign in to view PAQVERO Credit
        </button>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen pb-24 pt-28" data-testid="credit-loading">
        <div className="mx-auto max-w-6xl animate-pulse px-6 lg:px-12">
          <div className="h-3 w-24 rounded bg-hover" />
          <div className="mt-3 h-9 w-56 rounded-lg bg-hover" />
          <div className="mt-2 h-4 w-72 rounded bg-hover" />
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="rounded-2xl border border-line bg-surface p-5 shadow-card">
                <div className="h-3 w-20 rounded bg-hover" />
                <div className="mt-3 h-7 w-24 rounded-lg bg-hover" />
              </div>
            ))}
          </div>
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="h-48 rounded-2xl border border-line bg-surface shadow-card" />
            <div className="h-48 rounded-2xl border border-line bg-surface shadow-card lg:col-span-2" />
          </div>
        </div>
      </div>
    );
  }

  if (data.credit_status === "NOT_ELIGIBLE") {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 pb-24 pt-28" data-testid="credit-not-eligible">
        <div className="max-w-md text-center">
          <Landmark className="mx-auto h-10 w-10 text-amber-500" />
          <h1 className="font-display mt-5 text-3xl font-semibold tracking-tight">PAQVERO Credit</h1>
          {data.application?.status === "PENDING_REVIEW" || data.application?.status === "MORE_INFO" ? (
            <>
              <span className="mt-4 inline-block rounded-full border border-amber-500/40 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-400">Pending Review</span>
              <p className="mt-4 text-sm leading-relaxed text-2">
                Your application has been submitted. PAQVERO will review your request and notify you once it's decided.
              </p>
            </>
          ) : (
            <>
              <p className="mt-4 text-sm leading-relaxed text-2">
                30-day payment terms may be available for approved business customers.
              </p>
              <button onClick={() => navigate("/account/credit/apply")} className="mt-7 rounded-full bg-amber-500 px-8 py-4 text-sm font-bold text-black transition-colors hover:bg-amber-400" data-testid="credit-apply-cta">
                Apply for PAQVERO Credit →
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const suspended = data.credit_status === "SUSPENDED";
  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="account-credit-page">
      <div className="mx-auto max-w-6xl px-6 lg:px-12">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-3">Your Account</p>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="font-display text-4xl font-semibold tracking-tighter">PAQVERO Credit</h1>
          <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${suspended ? "border-red-500/40 text-red-400" : "border-emerald-500/40 text-emerald-400"}`} data-testid="credit-status-chip">
            {suspended ? "Suspended" : "Approved"}
          </span>
        </div>
        <p className="mt-2 text-sm text-3">{data.payment_term_days}-day payment terms on PAQVERO purchases.</p>

        {suspended && (
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-5" data-testid="credit-suspended-banner">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
            <p className="text-sm text-2">
              PAQVERO Credit temporarily unavailable. Please clear your outstanding balance to continue using credit.
              You can still pay now with UPI, cards or net banking.
            </p>
          </div>
        )}
        {data.overdue > 0 && (
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-5" data-testid="credit-overdue-banner">
            <Clock className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
            <p className="text-sm text-2">
              You have an overdue balance of <span className="font-bold text-red-400">{fmt(data.overdue)}</span>. Please settle it to keep your credit account in good standing.
            </p>
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="credit-dashboard">
          <StatCard label="Credit Limit" value={fmt(data.credit_limit)} testid="credit-limit" />
          <StatCard label="Available Credit" value={fmt(data.available_credit)} tone="text-emerald-400" testid="credit-available" />
          <StatCard label="Outstanding" value={fmt(data.outstanding)} tone={data.outstanding > 0 ? "text-amber-400" : "text-1"} testid="credit-outstanding" />
          <StatCard label="Overdue" value={fmt(data.overdue)} tone={data.overdue > 0 ? "text-red-400" : "text-1"} testid="credit-overdue" />
          <StatCard label="Next Payment Due" value={data.next_due_date ? fmtDate(data.next_due_date) : "—"} testid="credit-next-due" />
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <section className="rounded-2xl border border-line bg-surface p-6 shadow-card" data-testid="credit-pay-panel">
            <h3 className="font-display text-lg font-medium tracking-tight">Pay PAQVERO Credit</h3>
            {data.outstanding > 0 ? (
              <div className="mt-4 space-y-3">
                <input
                  data-testid="credit-pay-amount"
                  type="number" min="1" step="0.01" value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder={`Amount (up to ${fmt(data.outstanding)})`}
                  className="h-12 w-full rounded-xl border border-line bg-input px-4 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
                />
                <div className="flex gap-2">
                  {["upi", "card", "netbanking"].map((m) => (
                    <button key={m} data-testid={`credit-pay-method-${m}`} onClick={() => setPayMethod(m)}
                      className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold uppercase transition-colors ${payMethod === m ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-line text-3"}`}>
                      {m === "upi" ? "UPI" : m === "card" ? "Card" : "Net Banking"}
                    </button>
                  ))}
                </div>
                <button onClick={pay} disabled={paying} data-testid="credit-pay-submit"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3.5 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
                  {paying && <Loader2 className="h-4 w-4 animate-spin" />}
                  Pay {payAmount > 0 ? fmt(parseFloat(payAmount) || 0) : "Outstanding"}
                </button>
                <p className="text-[11px] leading-relaxed text-3">Paid via the same secure payment layer as checkout. Test mode — no real charge.</p>
              </div>
            ) : (
              <div className="mt-4 flex items-center gap-2.5 text-sm text-emerald-400">
                <ShieldCheck className="h-5 w-5" /> Nothing due — your credit balance is clear.
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-line bg-surface p-6 shadow-card lg:col-span-2" data-testid="credit-statement">
            <h3 className="font-display text-lg font-medium tracking-tight text-1">Credit Statement</h3>
            {!statement || statement.entries.length === 0 ? (
              <p className="mt-4 text-sm text-3">No credit transactions yet. Your orders on PAQVERO Credit will appear here.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm" data-testid="credit-statement-table">
                  <thead>
                    <tr className="border-b border-line text-[10px] font-bold uppercase tracking-[0.15em] text-3">
                      <th className="py-2.5 pr-4">Date</th><th className="py-2.5 pr-4">Transaction</th><th className="py-2.5 pr-4">Order</th>
                      <th className="py-2.5 pr-4 text-right">Debit</th><th className="py-2.5 pr-4 text-right">Payment</th>
                      <th className="py-2.5 pr-4 text-right">Balance</th><th className="py-2.5 pr-4">Due</th><th className="py-2.5 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.entries.map((e) => (
                      <tr key={e.id} className="border-b border-line" data-testid={`statement-row-${e.id}`}>
                        <td className="py-3 pr-4 text-2">{fmtDate(e.date)}</td>
                        <td className="py-3 pr-4 text-2">{e.kind === "debit" ? "Credit purchase" : e.kind === "payment" ? "Payment received" : "Adjustment"}{e.note && <span className="block text-[11px] text-3">{e.note}</span>}</td>
                        <td className="py-3 pr-4 font-mono text-xs text-2">
                          {e.order_number ? <Link to={`/orders/${e.order_id}`} className="text-amber-400 hover:text-amber-300">#{e.order_number}</Link> : "—"}
                        </td>
                        <td className="py-3 pr-4 text-right text-1">{e.debit != null ? fmt(e.debit) : "—"}</td>
                        <td className="py-3 pr-4 text-right text-emerald-400">{e.payment != null ? `−${fmt(e.payment)}` : "—"}</td>
                        <td className="py-3 pr-4 text-right font-bold text-1">{fmtSigned(e.balance)}</td>
                        <td className="py-3 pr-4 text-2">{e.due_date ? fmtDate(e.due_date) : "—"}</td>
                        <td className="py-3 text-right">
                          <span className={`whitespace-nowrap rounded border px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLES[e.status] || STATUS_STYLES.APPLIED}`}>{e.status === "PARTIAL" ? "PARTIALLY PAID" : e.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
